import React, { useEffect, useState, useContext } from 'react';
import {
  deleteFromLocalStorage,
  ensureValidUri,
  flattenObjectWithChar,
  getNumberFromSessionStorage,
  getPPAuthToken,
  getPPUrl,
  hasValue,
  isNullOrUndefined,
  setNumberInSessionStorage,
  safeParseJSON,
} from '@r-and-a-shared-ui/utils';

import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CTA,
  EnvironmentContext,
  LoadingSpinner,
  notifications,
  usePlayerPortalsConfigContext,
} from '@r-and-a-shared-ui/common-ui';
import classNames from 'classnames';
import {
  AUTH_TOKEN_KEY,
  CURRENT_CHAMPIONSHIP_ID_KEY,
  DEFAULT_VALUES_QUERY_KEY,
  CHAMPIONSHIP_PLAYER_KEY,
  ACCREDITATION_FORM_KONTENT_ITEM_CODENAME,
  CHAMPIONSHIP_CADDIE_LINKED_PLAYER_KEY,
  CHAMPIONSHIP_CADDIE_GUID_KEY,
  FormContentContext,
  FormFooter,
  FormFooterProps,
  FormHeader,
  FormStep,
  FormStepProps,
  KONTENT_ITEM_CODENAME_ATTRIBUTE_KEY,
  STORAGE,
  AVAILABLE_CHAMPIONSHIPS_ATTRIBUTE_KEY,
} from '@r-and-a-shared-ui/common-forms-ui';
import { getValidationSchema } from '../../fields/config/form-validation-schema';
import {
  getDefaultValues,
  getDefaultValuesInitialFormState,
  setFormStateFromSessionStorage,
} from '../../helpers/form-helpers';
import { pp_championshipPlayerDefaultValuesUpdate } from '@r-and-a-shared-ui/randa-trpc-server/input-schemas';
import SimplePopUp from '@r-and-a-shared-ui/common-ui/src/lib/SimplePopUp/SimplePopUp';
import PlayerPortalsFormStepPreview from '../PlayerPortalsFormStepPreview/PlayerPortalsFormStepPreview';
import { PlayerPortalsFormProps, PPFormTypes } from '../../types/ppTypes';
import { useActionContext } from '../../context/useActionsContext';
import {
  getStepIndexKey,
  setSpecialFieldsValues,
} from '../../helpers/form-steps-helper';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { getPPContactToDisplay } from '@r-and-a-shared-ui/common-ui';

import styles from './PlayerPortalsForm.module.scss';

export function PlayerPortalsForm(props: PlayerPortalsFormProps) {
  const {
    formCodename,
    formHeaderData: defaultHeaderData,
    steps,
    formFooterData,
    errorsDictionary,
    successMessagesDictionary,
    nextStepsAfterSubmit,
    backgroundImage,
    formType,
    defaultValuesJson,
    defaultValuesCallbackUrl,
    defaultValuesEntity,
    onDefaultValuesFetched,
    onFormClosed,
    customId,
    customClassname,
    isAccreditationForm = false,
    isCreateCaddieForm = false,
    selectedChampionshipPlayerId,
    selectedChampionshipCaddieId,
    accreditationFormOpenBtnLabel = '',
    accreditationFormKontentItemCodename = '',
    kontentItemId,
    hasFetchedAccreditations = false,
  } = props;

  const withModalAndPreview = formType === PPFormTypes.WithPreviewAndModal;

  const [currentFormStep, setCurrentFormStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const stepIndexKey = getStepIndexKey(formCodename);

  const context = useActionContext();
  const { API } = context || {};
  const { Image } = useContext(EnvironmentContext);
  const [allowedToFetch, setAllowedToFetch] = useState(false);
  const [defValuesQueryRes, setDefValuesQueryRes] = useState(undefined);

  const { siteSettings, currentChampionship } = usePlayerPortalsConfigContext();
  const availableChampionships =
    siteSettings?.playerPortals?.availableChampionships ?? [];

  const {
    formState,
    formState: { errors },
    getValues,
    setValue,
    reset,
    ...methods
  } = useForm({
    resolver: zodResolver(getValidationSchema(steps)),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues({
      callbackUrl: defaultValuesCallbackUrl,
      hardcodedDefaultValues: pp_championshipPlayerDefaultValuesUpdate,
      predefinedJSONObj: defaultValuesJson,
    }),
  });

  const callBackUrl = ensureValidUri(defaultValuesCallbackUrl, getPPUrl());

  const mutation = API?.[DEFAULT_VALUES_QUERY_KEY]?.useMutation();
  const defaultValuesAsync = mutation?.mutateAsync;
  const authTokenMutation = API?.[AUTH_TOKEN_KEY]?.useMutation();

  const userId = customId ?? getPPContactToDisplay(defaultValuesEntity)?.id;

  useEffect(() => {
    setIsLoading(true);
    const fetchDefaultValues = async () => {
      if (!allowedToFetch || !callBackUrl || !defaultValuesAsync) {
        setIsLoading(false);
        return;
      }

      const bearerToken = await getPPAuthToken(authTokenMutation.mutateAsync);
      defaultValuesAsync({
        callBackUrl,
        bearerToken,
        id: userId,
      })
        .then((res: any) => {
          setDefValuesQueryRes(res);
          if (onDefaultValuesFetched) {
            onDefaultValuesFetched(res);
          }
        })
        .catch((err: any) => {
          console.error(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    fetchDefaultValues();
  }, [allowedToFetch, callBackUrl, defaultValuesAsync]);

  useEffect(() => {
    let defaultValues = undefined;
    if (mutation?.status === 'success' && defValuesQueryRes) {
      defaultValues = flattenObjectWithChar(defValuesQueryRes, '_');
    } else {
      if (hasValue(defaultValuesJson) && defaultValuesJson !== undefined) {
        const defValuesParsed = safeParseJSON(defaultValuesJson);
        defaultValues = flattenObjectWithChar(defValuesParsed, '_');
      }
    }
    if (defaultValues !== undefined) {
      setSpecialFieldsValues(defaultValues, '_', steps, defValuesQueryRes);
      reset(defaultValues);
      setValue(KONTENT_ITEM_CODENAME_ATTRIBUTE_KEY, formCodename);
      setValue(AVAILABLE_CHAMPIONSHIPS_ATTRIBUTE_KEY, availableChampionships);
    }
  }, [defValuesQueryRes]);

  useEffect(() => {
    if (mutation?.status === 'success' || !defaultValuesCallbackUrl) {
      setAllowedToFetch(false);
    } else {
      setAllowedToFetch(true);
    }
  }, [mutation?.status, defaultValuesCallbackUrl]);

  useEffect(() => {
    setValue(KONTENT_ITEM_CODENAME_ATTRIBUTE_KEY, formCodename);
    setValue(AVAILABLE_CHAMPIONSHIPS_ATTRIBUTE_KEY, availableChampionships);
  }, [formCodename, setValue]);

  useEffect(() => {
    if (!isCreateCaddieForm || !hasValue(selectedChampionshipPlayerId)) {
      return;
    }

    setValue(
      CHAMPIONSHIP_CADDIE_LINKED_PLAYER_KEY,
      selectedChampionshipPlayerId,
    );

    setValue(CHAMPIONSHIP_CADDIE_GUID_KEY, selectedChampionshipCaddieId);
  }, [isCreateCaddieForm, selectedChampionshipPlayerId, setValue]);

  const stepSetter = () => {
    const stepNumber = getNumberFromSessionStorage(stepIndexKey);
    const totalSteps = steps?.length || 0;

    if (currentFormStep >= totalSteps) {
      return;
    }

    setCurrentFormStep(stepNumber);
  };

  window.addEventListener(STORAGE, stepSetter);

  useEffect(() => {
    setNumberInSessionStorage(stepIndexKey, 0);
    setFormStateFromSessionStorage(setValue);
    window.dispatchEvent(new Event(STORAGE));
    return () => {
      deleteFromLocalStorage(stepIndexKey);
      window.removeEventListener(STORAGE, stepSetter);
    };
  }, [setValue]);

  const notifyError = (message: string) => notifications.errorSmall(message);
  const notifySuccess = (message: string) =>
    notifications.successSmall(message);

  useEffect(() => {
    const errorMessage = errors?.root?.serverError?.message;
    if (!isNullOrUndefined(errorMessage)) {
      notifyError(errorMessage);
    }
  }, [errors?.root?.serverError]);

  useEffect(() => {
    if (submitSuccessMessage) {
      notifySuccess(submitSuccessMessage);
    }
  }, [submitSuccessMessage]);

  const renderFormStep = (
    formStepProps: FormStepProps[] | undefined,
  ): React.JSX.Element | undefined => {
    if (
      !hasValue(formStepProps) ||
      typeof formStepProps === 'undefined' ||
      currentFormStep >= formStepProps.length
    ) {
      return undefined;
    }
    return (
      <FormStep
        {...formStepProps[currentFormStep]}
        withModalAndPreview={withModalAndPreview}
        key={formStepProps[currentFormStep]?.key}
      />
    );
  };

  const renderFormHeader = (): React.JSX.Element | undefined => {
    const formHeaderData = steps
      ? steps[currentFormStep]?.customHeader
      : defaultHeaderData;

    if (isNullOrUndefined(formHeaderData)) {
      return undefined;
    }
    return <FormHeader {...formHeaderData} />;
  };

  const renderFormFooter = (
    formFooterData: FormFooterProps | undefined,
  ): React.JSX.Element | undefined => {
    if (isNullOrUndefined(formFooterData)) {
      return undefined;
    }
    return <FormFooter {...formFooterData} />;
  };
  // TO_DO For online entries add the 3 additional fields in the form
  const renderPreviewSteps = () => {
    if (!steps) {
      return null;
    }
    return steps.map((stepProps, index) => {
      if (!hasValue(stepProps) || typeof stepProps === 'undefined') {
        return undefined;
      }
      return (
        <PlayerPortalsFormStepPreview
          {...stepProps}
          stepIndexKey={stepIndexKey}
          key={stepProps?.key}
          onClickEditHandler={onClickEditHandler}
          stepNumber={index}
        />
      );
    });
  };

  const renderOpenFormCTA = (label: string) => {
    return (
      <CTA
        onClick={onClickEditHandler}
        label={label}
        isDisabled={!hasFetchedAccreditations}
      />
    );
  };

  const resetModalAndForm = () => {
    setIsModalOpen(false);
    setIsDialogOpen(false);
    reset();
    setNumberInSessionStorage(stepIndexKey, 0);
    window.dispatchEvent(new Event(STORAGE));
    setAllowedToFetch(true);
    onFormClosed?.();
  };

  const onCloseFormModal = (openDialog = true) => {
    if (openDialog) {
      setIsDialogOpen(true);
      return;
    }
    resetModalAndForm();
  };

  const onClickEditHandler = () => {
    if (isAccreditationForm && customId) {
      setValue(CHAMPIONSHIP_PLAYER_KEY, customId);
      setValue(
        CURRENT_CHAMPIONSHIP_ID_KEY,
        currentChampionship?.championshipIdentifier,
      );
      setValue(
        ACCREDITATION_FORM_KONTENT_ITEM_CODENAME,
        accreditationFormKontentItemCodename,
      );
    }
    setIsModalOpen(true);
  };

  const renderPreviewAndModalForm = (): React.JSX.Element | undefined => {
    return (
      <div data-testid="popup">
        <div
          id={styles[customClassname || '']}
          className={`${styles['form__preview']}`}
        >
          {isAccreditationForm
            ? renderOpenFormCTA(accreditationFormOpenBtnLabel)
            : renderPreviewSteps()}
        </div>
        <SimplePopUp isVisible={isModalOpen} onClose={onCloseFormModal}>
          {renderClassicForm()}
        </SimplePopUp>
        <ConfirmationDialog
          isDialogOpen={isDialogOpen}
          onConfirm={resetModalAndForm}
          onClose={() => setIsDialogOpen(false)}
        />
      </div>
    );
  };

  const renderClassicForm = (): React.JSX.Element => {
    return (
      <form className={customClassname} data-kontent-item-id={kontentItemId}>
        <div className={styles.form}>
          <div className={styles['form__wrapper']}>
            <div
              className={classNames(
                styles['form__step'],
                styles[`form__step--${currentFormStep}`],
              )}
            >
              {backgroundImage && (
                <Image
                  src={backgroundImage}
                  alt="Form background image"
                  className={styles['form__background']}
                  width={300}
                  height={300}
                />
              )}
              {renderFormHeader()}
              {isLoading && <LoadingSpinner />}
              <div
                className={`${styles['form__content--body']} ${
                  isLoading ? styles.loading : ''
                }`}
              >
                {renderFormStep(steps)}
              </div>
            </div>
            {renderFormFooter(formFooterData)}
          </div>
        </div>
      </form>
    );
  };

  return (
    <FormContentContext.Provider
      value={{
        formContentProps: props,
        currentFormStep,
        isLoading,
        errorsDictionary,
        setSubmitSuccessMessage,
        successMessagesDictionary,
        nextStepsAfterSubmit,
        customClientCTAHandler: onCloseFormModal,
        initialFormState: getDefaultValuesInitialFormState({
          queryRes: defValuesQueryRes,
          predefinedJSONObj: defaultValuesJson,
        }),
      }}
    >
      {isLoading && <LoadingSpinner />}
      <FormProvider
        {...methods}
        reset={reset}
        getValues={getValues}
        setValue={setValue}
        formState={formState}
      >
        {withModalAndPreview
          ? renderPreviewAndModalForm()
          : renderClassicForm()}
      </FormProvider>
    </FormContentContext.Provider>
  );
}

export default PlayerPortalsForm;
